import React, { useMemo } from 'react';
import CookieHelper from '../../Helpers/cookieHelper';

// New Year's Eve loading backdrop (shared / observed across offices).
// Fireworks bursting over a city skyline, with the BARE mark composited
// centred on top by the loader frame. Motion-heavy on purpose — staggered
// firework bursts + falling confetti + twinkling windows.
//
// Confetti drift is baked into the keyframe (not a CSS custom property), so
// the file stays plain-JS .tsx (TS's CSSProperties rejects `--foo` keys).

const SKIES = ['mid', 'indigo', 'royal', 'plum'];

// Each set is a coherent colour family so a reroll visibly changes the whole
// display, rather than a similar multicolour mix every time.
const BURST_SETS = [
    ['#ffd24d', '#ffb02e', '#fff0a8'], // gold gala
    ['#4fd1ff', '#8a5cff', '#bfe8ff'], // cool neon
    ['#ff5bb0', '#ff7a5f', '#ffd24d'], // hot festive
    ['#4fcf7d', '#a8ffd8', '#ffd24d'], // mint + gold
];

const CONFETTI_COLORS = ['#ffd24d', '#ff5bb0', '#4fd1ff', '#4fcf7d', '#ff7a5f'];

// Active on Dec 31, or when the shared holiday-loader opt-in cookie is set.
export function isNewYearsEveActive()
{
    if (CookieHelper.getCookie('holidayloaders', 'false') === 'true') return true;
    const d = new Date();
    return d.getMonth() === 11 && d.getDate() === 31;
}

const pick = (a) => a[Math.floor(Math.random() * a.length)];

function rollTraits()
{
    return { sky: pick(SKIES), bursts: pick(BURST_SETS) };
}

function Burst({ x, y, color, delay, scale = 1 })
{
    const rays = [];
    const n = 12;
    for (let i = 0; i < n; i++)
    {
        const rot = `rotate(${(i * 360) / n})`;
        rays.push(<line key={'l' + i} x1="0" y1="0" x2="0" y2="-15" stroke={color} strokeWidth="1.6" strokeLinecap="round" transform={rot} />);
        rays.push(<circle key={'d' + i} cx="0" cy="-16" r="1.7" fill={color} transform={rot} />);
    }
    return (
        <g transform={`translate(${x},${y}) scale(${scale})`}>
            <g className="nye-burst" style={{ animationDelay: delay }}>
                <circle r="1.8" fill="#fff" />
                {rays}
            </g>
        </g>
    );
}

// Skyline buildings; some windows glow (staggered). Base sits on y=220.
const BUILDINGS = [
    { x: 0, w: 40, h: 52 }, { x: 34, w: 30, h: 78 }, { x: 60, w: 34, h: 44 },
    { x: 92, w: 26, h: 66 }, { x: 116, w: 38, h: 96 }, { x: 152, w: 30, h: 58 },
    { x: 180, w: 34, h: 100 }, { x: 212, w: 28, h: 62 }, { x: 238, w: 40, h: 84 },
    { x: 276, w: 30, h: 50 }, { x: 302, w: 34, h: 74 }, { x: 332, w: 32, h: 56 },
];

function Skyline()
{
    const parts = [];
    let wi = 0;
    BUILDINGS.forEach((b, bi) =>
    {
        const top = 220 - b.h;
        parts.push(<rect key={'b' + bi} x={b.x} y={top} width={b.w} height={b.h} fill="rgba(6,10,26,.78)" />);
        for (let row = 0; row < Math.floor(b.h / 16); row++)
        {
            for (let col = 0; col < Math.floor(b.w / 12); col++)
            {
                const wx = b.x + 5 + col * 12;
                const wy = top + 8 + row * 16;
                const glow = (wi % 3 === 0);
                parts.push(
                    <rect
                        key={'w' + bi + '-' + row + '-' + col}
                        className={glow ? 'nye-win' : undefined}
                        x={wx} y={wy} width="4.5" height="6" rx="1"
                        fill="#ffd98a"
                        opacity={glow ? undefined : 0.28}
                        style={glow ? { animationDelay: ((wi % 7) * 0.4) + 's' } : undefined}
                    />,
                );
                wi++;
            }
        }
    });
    return <g>{parts}</g>;
}

const STARS = [
    [30, 30], [80, 20], [140, 34], [190, 18], [250, 28], [310, 22], [340, 44], [20, 60], [356, 74],
];

const CONFETTI = [
    { x: 40, dur: '3.6s', delay: '0s' },
    { x: 90, dur: '4.2s', delay: '1.1s' },
    { x: 150, dur: '3.9s', delay: '0.5s' },
    { x: 210, dur: '4.6s', delay: '2.0s' },
    { x: 270, dur: '3.4s', delay: '1.5s' },
    { x: 320, dur: '4.1s', delay: '0.8s' },
    { x: 120, dur: '4.8s', delay: '2.6s' },
    { x: 300, dur: '3.7s', delay: '3.1s' },
];

export function NewYearsEveScene()
{
    const t = useMemo(() => rollTraits(), []);
    const c = t.bursts;
    return (
        <span className={'hol-stage hol-sky-nye-' + t.sky} role="img" aria-label="Fireworks over a city skyline for New Year's Eve">
            <svg className="hol-svg" viewBox="0 0 360 220" preserveAspectRatio="xMidYMid meet">
                <g fill="#fff">
                    {STARS.map((s, i) => (
                        <circle key={i} className="nye-star" cx={s[0]} cy={s[1]} r="1.3" style={{ animationDelay: (i * 0.28) + 's' }} />
                    ))}
                </g>
                <Skyline />
                <Burst x={70} y={54} color={c[0]} delay="0s" />
                <Burst x={288} y={48} color={c[1]} delay="0.8s" />
                <Burst x={180} y={34} color={c[2]} delay="1.5s" scale={1.15} />
                <Burst x={40} y={104} color={c[1]} delay="1.1s" scale={0.8} />
                <Burst x={322} y={104} color={c[0]} delay="0.4s" scale={0.8} />
                {CONFETTI.map((cf, i) => (
                    <g key={i} className="nye-conf" style={{ animationDuration: cf.dur, animationDelay: cf.delay }}>
                        <rect x={cf.x} y="6" width="4" height="6" rx="1" fill={CONFETTI_COLORS[i % CONFETTI_COLORS.length]} />
                    </g>
                ))}
            </svg>
        </span>
    );
}
